/**
 * Shipping line canonical SCAC table.
 *
 * Mirror of backend/src/constants/shippingLines.js — keep the two in sync
 * when adding carriers. DB stores the canonical 4-letter SCAC (`code`);
 * the UI renders the full name. See the backend file for background on
 * why terminal short codes get normalized at the adapter layer.
 */

export interface ShippingLine {
  /** Official 4-letter SCAC. Canonical value written to DB. */
  code: string;
  /** Human-readable name shown in dropdowns. */
  fullName: string;
  /** Typical short form printed by terminals (informational). */
  short: string;
  /** Every form seen in terminal output. Includes `code`. */
  aliases: string[];
}

export const SHIPPING_LINES: readonly ShippingLine[] = [
  // ---- Major transpacific carriers ----
  { code: 'MAEU', fullName: 'Maersk Line',                short: 'Maersk',      aliases: ['MSK', 'MAE', 'MAEU', 'SEAU', 'SEALAND'] },
  { code: 'MEDU', fullName: 'MSC',                        short: 'MSC',         aliases: ['MSC', 'MEDU', 'MSCU'] },
  { code: 'CMDU', fullName: 'CMA CGM',                    short: 'CMA',         aliases: ['CMA', 'CMD', 'CMDU'] },
  { code: 'COSU', fullName: 'COSCO Shipping',             short: 'COSCO',       aliases: ['COS', 'COSU', 'CSNE'] },
  { code: 'HLCU', fullName: 'Hapag-Lloyd',                short: 'Hapag-Lloyd', aliases: ['HPL', 'HLC', 'HLCU'] },
  { code: 'ONEY', fullName: 'Ocean Network Express (ONE)', short: 'ONE',        aliases: ['ONE', 'ONEY'] },
  { code: 'EGLV', fullName: 'Evergreen Line',             short: 'Evergreen',   aliases: ['EGV', 'EMC', 'EGLV'] },
  { code: 'HDMU', fullName: 'HMM (Hyundai)',              short: 'HMM',         aliases: ['HDM', 'HMM', 'HDMU', 'HMMU'] },
  { code: 'YMLU', fullName: 'Yang Ming Marine',           short: 'Yang Ming',   aliases: ['YML', 'YMLU'] },
  { code: 'ZIMU', fullName: 'ZIM',                        short: 'ZIM',         aliases: ['ZIM', 'ZIMU'] },
  { code: 'WHLC', fullName: 'Wan Hai Lines',              short: 'Wan Hai',     aliases: ['WHL', 'WHLC'] },
  { code: 'OOLU', fullName: 'OOCL',                       short: 'OOCL',        aliases: ['OOL', 'OOLU'] },
  { code: 'MATS', fullName: 'Matson',                     short: 'Matson',      aliases: ['MAT', 'MATS'] },

  // ---- Other carriers calling US ports ----
  { code: 'APLU', fullName: 'APL (American President Lines)', short: 'APL',    aliases: ['APL', 'APLU'] },
  { code: 'ANNU', fullName: 'ANL Container Line',         short: 'ANL',         aliases: ['ANL', 'ANNU'] },
  { code: 'SUDU', fullName: 'Hamburg Süd',                short: 'Hamburg Sud', aliases: ['SUD', 'SUDU', 'HAMBURG SUD'] },
  { code: 'PCIU', fullName: 'Pacific International Lines (PIL)', short: 'PIL', aliases: ['PIL', 'PCIU'] },
  { code: 'SMLM', fullName: 'SM Line',                    short: 'SM Line',     aliases: ['SML', 'SMLM'] },
  { code: 'GSLU', fullName: 'Gold Star Line',             short: 'Gold Star',   aliases: ['GSL', 'GSLU'] },
  { code: 'ESPU', fullName: 'Emirates Shipping Line',     short: 'Emirates',    aliases: ['ESL', 'ESPU'] },
  { code: 'CHVW', fullName: 'Swire Shipping',             short: 'Swire',       aliases: ['SWI', 'CHVW'] },
  { code: 'WWSU', fullName: 'Westwood Shipping Lines',    short: 'Westwood',    aliases: ['WWS', 'WWSU'] },
  { code: 'CMCU', fullName: 'Crowley',                    short: 'Crowley',     aliases: ['CRO', 'CMCU', 'CAMN'] },
  { code: 'KOSL', fullName: 'King Ocean Services',        short: 'King Ocean',  aliases: ['KOS', 'KOSL'] },
  { code: 'SMLU', fullName: 'Seaboard Marine',            short: 'Seaboard',    aliases: ['SMLU', 'SEABOARD'] },
  { code: 'TRKU', fullName: 'Turkon Line',                short: 'Turkon',      aliases: ['TRK', 'TRKU'] },
  { code: 'ARKU', fullName: 'Arkas Line',                 short: 'Arkas',       aliases: ['ARK', 'ARKU'] },
  { code: 'ACLU', fullName: 'Atlantic Container Line',    short: 'ACL',         aliases: ['ACL', 'ACLU'] },
  { code: 'NSAU', fullName: 'Bahri (NSCSA)',              short: 'Bahri',       aliases: ['NSA', 'NSAU', 'BAHRI'] },
  { code: 'PSHI', fullName: 'Pasha Hawaii',               short: 'Pasha',       aliases: ['PSH', 'PSHI'] },
  { code: 'TOTE', fullName: 'TOTE Maritime',              short: 'TOTE',        aliases: ['TOTE'] },
  { code: 'KMTU', fullName: 'KMTC (Korea Marine Transport)', short: 'KMTC',     aliases: ['KMT', 'KMTU', 'KMTC'] },
  { code: 'SKLU', fullName: 'Sinokor Merchant Marine',    short: 'Sinokor',     aliases: ['SKL', 'SKLU'] },
  { code: 'MCCQ', fullName: 'MCC Transport',              short: 'MCC',         aliases: ['MCC', 'MCCQ'] },
  { code: 'SAFM', fullName: 'Safmarine',                  short: 'Safmarine',   aliases: ['SAF', 'SAFM'] },
  { code: 'KKLU', fullName: '"K" Line (Kawasaki Kisen)',  short: 'K Line',      aliases: ['KKL', 'KKLU', 'K LINE', 'K-LINE'] },
] as const;

const ALIAS_TO_CODE: Readonly<Record<string, string>> = (() => {
  const map: Record<string, string> = {};
  for (const line of SHIPPING_LINES) {
    for (const alias of line.aliases) map[alias.toUpperCase()] = line.code;
    map[line.fullName.toUpperCase()] = line.code;
    map[line.short.toUpperCase()] = line.code;
  }
  return Object.freeze(map);
})();

/**
 * Normalize any stored / scraped shipping-line value to its canonical SCAC.
 * Unknown values pass through uppercased (never dropped).
 */
export function normalizeScac(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const upper = String(raw).trim().toUpperCase();
  if (!upper) return null;
  return ALIAS_TO_CODE[upper] || upper;
}

/**
 * Look up the display-ready shipping line entry for a stored code.
 * Returns null for unknown codes so callers can fall back to the raw value.
 */
export function findShippingLineByCode(code: string | null | undefined): ShippingLine | null {
  if (!code) return null;
  const normalized = normalizeScac(code);
  return SHIPPING_LINES.find(l => l.code === normalized) || null;
}
